import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "./api";
import { buildConnectorsIndex, buildEdges } from "./graph";
import { buildConversationUpdatePayload } from "./conversationPayload";

const NODE_WIDTH = 400;
const NODE_HORIZONTAL_GAP = 650;
const NODE_START_X = 100;
const NODE_START_Y = 100;

// Owns all data-fetching and derived state for the conversation canvas:
// the conversation itself, its questions/connectors/edges, the estimation
// groups and pipeline stages available for connectors, and the node
// positions handed to the canvas.
export default function useConversationCanvas(conversationId) {
    const [conversation, setConversation] = useState();
    const [questions, setQuestions] = useState();
    const [connectors, setConnectors] = useState();
    const [estimationGroups, setEstimationGroups] = useState();
    const [stages, setStages] = useState();
    const [botErrors, setBotErrors] = useState();

    const loadConversation = useCallback(() => {
        return api
            .getConversation(conversationId)
            .then((response) => setConversation(response.data))
            .catch((err) => console.error(err));
    }, [conversationId]);

    const loadQuestions = useCallback(() => {
        return api
            .getQuestions(conversationId)
            .then((response) => {
                setQuestions(response.data);
                setConnectors(buildConnectorsIndex(response.data));
            })
            .catch((err) => console.error(err));
    }, [conversationId]);

    const loadEstimationGroups = useCallback(() => {
        return api
            .getEstimationGroups(conversationId)
            .then((response) => setEstimationGroups(response.data))
            .catch((err) => console.error(err));
    }, [conversationId]);

    const loadBotErrors = useCallback(() => {
        return api
            .getBotErrorsForConversation(conversationId)
            .then((response) => setBotErrors(response.data))
            .catch((err) => console.error(err));
    }, [conversationId]);

    useEffect(() => {
        if (!conversationId) return;
        loadConversation();
        loadQuestions();
        loadEstimationGroups();
        loadBotErrors();
    }, [conversationId, loadConversation, loadQuestions, loadEstimationGroups, loadBotErrors]);

    useEffect(() => {
        if (!conversation || !conversation.stageTypeId) return;
        api.getStages(conversation.stageTypeId.id)
            .then((response) => setStages(response.data))
            .catch((err) => console.error(err));
    }, [conversation]);

    const reloadQuestions = useCallback(() => {
        setConnectors(null);
        return loadQuestions();
    }, [loadQuestions]);

    const saveCoordinates = useCallback((node) => {
        if (!node) return;
        api.saveCoordinates(node).catch((err) => console.error(err));
    }, []);

    const createQuestion = useCallback(
        (text) => {
            if (!conversation) return Promise.reject(new Error("Conversation not loaded yet"));
            // Errors intentionally propagate to the caller (unlike the other
            // fetches above) so the "add question" form can stay open with
            // the typed text if the save fails, instead of clearing itself.
            return api.createQuestion({ text, conversationId: conversation.id }).then(() => reloadQuestions());
        },
        [conversation, reloadQuestions]
    );

    const updateConversation = useCallback(
        (changes) => {
            if (!conversation) return Promise.reject(new Error("Conversation not loaded yet"));
            const payload = buildConversationUpdatePayload(conversation, changes);
            return api.updateConversation(conversation.id, payload).then((response) => {
                setConversation(response.data);
                return response.data;
            });
        },
        [conversation]
    );

    const edges = useMemo(() => buildEdges(questions || [], connectors), [questions, connectors]);

    const initialNodes = useMemo(() => {
        if (!questions) return [];
        return questions.map((item, index) => ({
            id: item.id,
            x: item.x ? item.x : NODE_START_X + index * NODE_HORIZONTAL_GAP,
            y: item.y ? item.y : NODE_START_Y,
            width: NODE_WIDTH,
            item,
        }));
    }, [questions]);

    return {
        conversation,
        questions,
        edges,
        initialNodes,
        estimationGroups,
        stages,
        botErrors,
        reloadQuestions,
        reloadEstimationGroups: loadEstimationGroups,
        reloadConversation: loadConversation,
        saveCoordinates,
        createQuestion,
        updateConversation,
    };
}
